import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthToken } from '../models/auth.models';
import { Utils } from './utils';
import { AvailableApplication } from '../models/environment.interface';
import { DataService } from './data.service';
import {
  buildAuthAppLogoutUrl,
  consumeClinicSignOutCookie,
} from '../utils/cross-app-logout.util';
import { getLaunchRequestedCompanyId, setLaunchRequestedCompanyId } from '../utils/clinic-launch-context.util';
import { normalizeAuthAppUrl } from '../utils/auth-app-url.util';
import { isLocalhostDevHostname } from '../utils/localhost-dev.util';

/** Response from POST /api/v1/auth/identity/session/refresh (Aya / Entra / Firebase). */
export interface SessionRefreshResponse {
    status?: string;
    token?: string;
    refreshToken?: string;
    email?: string;
    expiresIn?: number;
    expiresAt?: string;
    tokenType?: string;
}

/** Row from GET /api/v1/services (admin API). */
export interface OrganizationServiceDto {
    id: string;
    serviceName: string;
    serviceType?: string;
    organizationId?: string;
}

@Injectable({ providedIn: 'root' })

export class AuthenticationService {
    private readonly sessionRefreshIntervalMs = 5 * 60 * 1000;
    private readonly tokenRefreshBufferMs = 5 * 60 * 1000;
    private sessionRefreshTimer: ReturnType<typeof setInterval> | null = null;
    private refreshSessionPromise: Promise<string | null> | null = null;
    private loginRedirectInProgress = false;

    constructor(
        private readonly _localStorageService: LocalStorageService,
        private readonly _dataService: DataService
    ) {
    }

    /** Used by ErrorInterceptor on 401 — identity session refresh only. */
    getNewAccessToken(refreshToken: string): Observable<AuthToken> {
        return this.refreshSessionViaIdentityApi(refreshToken).pipe(
            map((res) => ({
                accessToken: (res.token || '').trim(),
                refreshToken: (res.refreshToken || refreshToken || '').trim(),
            })),
            catchError((err) => {
                console.warn('[clinic-auth] identity session refresh failed', err);
                return throwError(() => err);
            })
        );
    }

    refreshSessionViaIdentityApi(
        refreshToken: string
    ): Observable<SessionRefreshResponse> {
        const url = `${this.adminApiBaseUrl}api/v1/auth/identity/session/refresh`;
        return this._dataService.post(url, { refreshToken });
    }

    getLoggedInUser(): Observable<User> {
        const url = this.adminApiBaseUrl + 'api/v1/auth/currentUser';
        return this._dataService.get(url);
    }

    getUserById(userId: string): Observable<User> {
        const url = `${this.adminApiBaseUrl}api/v1/users/${userId}`;
        return this._dataService.get(url);
    }

    getUserCompanies(userId: string): Observable<UserCompany[]> {
        const url = `${this.adminApiBaseUrl}api/v1/users/${userId}/companies`;
        return this._dataService.get(url);
    }

    /** GET /api/v1/users/referral-sources (admin API). Returns array of referral source options. */
    getReferralSources(): Observable<any[]> {
        const url = `${this.adminApiBaseUrl}api/v1/users/referral-sources`;
        return this._dataService.get(url);
    }

    /**
     * GET /api/v1/services (admin API). Raw rows include `serviceType`.
     * For UI dropdowns, prefer {@link ApplicationContext.organizationServices} (filtered by charts-v3 license).
     */
    getServices(): Observable<OrganizationServiceDto[]> {
        const url = `${this.adminApiBaseUrl}api/v1/services`;
        return this._dataService.get(url);
    }

    getUserLicenses(): Observable<CompanyProductLicense[]> {
        const url = `${this.adminApiBaseUrl}api/v1/companies/company_licenses`;
        return this._dataService.get(url);
    }

    getAuthTokenForCompany(userId: string, companyId: string): Observable<AuthToken> {
        setLaunchRequestedCompanyId(companyId);
        const accessToken = (this.authToken || '').trim();
        const refreshToken = (this.refreshToken || '').trim();
        if (!accessToken || this.isLegacyAccessToken(accessToken)) {
            return throwError(
                () => new Error('Identity session required to select a company.')
            );
        }
        return of({ accessToken, refreshToken });
    }

    hideOrPinCompany(request: any): Observable<void> {
        return this._dataService.post(`${this.adminApiBaseUrl}api/v1/users/pin-hide-company`, request);
    }

    logoutAllApps(): Observable<void> {
        return this._dataService.post(`${this.adminApiBaseUrl}api/v1/auth/logout`, null);
    }

    deviceSessionCheck(): Observable<AuthToken> {
        if (!this._localStorageService.machineId) {
            return of(null);
        }
        return this._dataService.get(`${this.adminApiBaseUrl}api/v1/auth/check-session/${this._localStorageService.machineId}`);
    }

    get authToken(): string {
        if (!this._localStorageService.authorizationData) {
            return null;
        }
        return this._localStorageService.authorizationData.token;
    }

    get refreshToken(): string {
        if (!this._localStorageService.authorizationData) {
            return null;
        }
        return this._localStorageService.authorizationData.refreshToken;
    }

    isLoginRedirectInProgress(): boolean {
        return this.loginRedirectInProgress;
    }

    get phiApiBaseUrl(): string {
        return environment.apiBaseUrl;
    }

    get adminApiBaseUrl(): string {
        return environment.adminApiBaseUrl;
    }

    logout(loggedOutFromAllApps: boolean = false): void {
        if (loggedOutFromAllApps) {
            this.logoutFromAllApps();
            return;
        }

        const authData = this._localStorageService.authorizationData;
        const hasSession =
            !!(authData?.token?.trim() || authData?.refreshToken?.trim());
        if (!hasSession) {
            this.clearSessionAndRedirectToLogin();
            return;
        }

        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;
        this._localStorageService.clearAllClientStorage();
        this.redirectToLogin(0, { signedOut: true });
    }

    /** Clear local Clinic session and send user to dev-auth without tearing down identity SSO. */
    clearSessionAndRedirectToLogin(): void {
        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;
        this._localStorageService.clearAllClientStorage();
        this.redirectToLogin(0);
    }

    /**
     * Sign out of Clinic and relay through auth SPA, ask-aya-health, then aya-health-chat.
     * Auth SPA runs logout-everywhere and clears Entra/Firebase server sessions.
     */
    logoutFromAllApps(): void {
        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;

        const authData = this._localStorageService.authorizationData;
        const token = (authData?.token || '').trim();
        const refreshToken = (authData?.refreshToken || '').trim();

        this._localStorageService.clearAllClientStorage();

        // Best-effort legacy session revoke; redirect must not wait on this.
        this.logoutAllApps().subscribe({ error: () => undefined });

        window.location.href = this.buildCrossAppLogoutUrl(token, refreshToken);
    }

    private buildCrossAppLogoutUrl(token: string, refreshToken: string): string {
        const authAppUrl = this.getClinicAuthEntryBaseUrl();
        return buildAuthAppLogoutUrl({
            authAppUrl,
            relayOrigins: this.logoutRelayOrigins(),
            finalReturnUrl: this.defaultLogoutLandingUrl(),
            everywhere: true,
            token: token || undefined,
            refreshToken: refreshToken || undefined,
        });
    }

    private defaultLogoutLandingUrl(): string {
        return this.buildClinicLoginUrl({ loggedOut: true });
    }

    /** Auth SPA entry for Clinic (`/?app=clinic`, not legacy dev-app). */
    buildClinicLoginUrl(options?: { loggedOut?: boolean }): string {
        const base = this.getClinicAuthEntryBaseUrl();
        const login = new URL(`${base.replace(/\/+$/, "")}/`);
        login.searchParams.set("app", AvailableApplication.ClinicV3);
        login.searchParams.delete("loggedOut");
        if (options?.loggedOut) {
            login.searchParams.set("loggedOut", "1");
        }
        return login.href;
    }

    /** Prefer auth SPA (dev-auth / qa-auth) over legacy loginUrl (dev-app). */
    private getClinicAuthEntryBaseUrl(): string {
        if (isLocalhostDevHostname()) {
            const fromEnv = normalizeAuthAppUrl(
                environment.authAppUrl?.trim() ||
                environment.loginUrl?.trim() ||
                ""
            );
            if (fromEnv && /localhost|127\.0\.0\.1/.test(fromEnv)) {
                return fromEnv;
            }
            return normalizeAuthAppUrl("https://localhost:4200");
        }
        return normalizeAuthAppUrl(
            environment.authAppUrl?.trim() ||
            environment.loginUrl?.trim() ||
            ""
        );
    }

    redirectToLogin(
        timeoutSecs: number = 1000,
        options?: { signedOut?: boolean },
    ) {
        if (this.loginRedirectInProgress) {
            return;
        }

        if (!options?.signedOut) {
            // Drop stale sign-out marker — never add loggedOut=1 for SSO handoff redirects.
            consumeClinicSignOutCookie();
        }

        const loginUrl = this.buildClinicLoginUrl(
            options?.signedOut ? { loggedOut: true } : undefined,
        );
        const navigate = () => {
            if (this.loginRedirectInProgress) {
                return;
            }
            this.loginRedirectInProgress = true;
            window.location.replace(loginUrl);
        };
        if (timeoutSecs <= 0) {
            navigate();
            return;
        }
        setTimeout(navigate, timeoutSecs);
    }

    private logoutRelayOrigins(): string[] {
        const origins: string[] = [];
        const clinic = this.normalizeAppUrl(environment.clinicV3AppUrl);
        if (clinic) {
            try {
                origins.push(new URL(clinic).origin);
            } catch {
                // ignore invalid clinic URL
            }
        }
        const aya = this.normalizeAppUrl(environment.ayaAppUrl);
        const www = this.normalizeAppUrl(environment.ayaHealthChatAppUrl);
        if (aya && www) {
            try {
                const ayaOrigin = new URL(aya).origin;
                const wwwOrigin = new URL(www).origin;
                if (ayaOrigin !== wwwOrigin && !origins.includes(ayaOrigin)) {
                    origins.push(ayaOrigin);
                }
            } catch {
                // ignore invalid URLs
            }
        }
        return origins;
    }

    private normalizeAppUrl(url: string | undefined): string | null {
        const trimmed = url?.trim();
        if (!trimmed) {
            return null;
        }
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            return trimmed.replace(/\/+$/, '');
        }
        return `https://${trimmed.replace(/\/+$/, '')}`;
    }

    get apiBaseUrl(): string {
        return environment.apiBaseUrl;
    }

    persistAuthData = (token: AuthToken): void => {
        if (this.isLegacyAccessToken(token.accessToken)) {
            console.warn('[clinic-auth] Refusing to persist legacy company-scoped JWT.');
            return;
        }
        let jwtPayload: { exp?: number } = {};
        try {
            jwtPayload = Utils.parseJwt(token.accessToken);
        } catch {
            jwtPayload = {};
        }
        const existing = this._localStorageService.authorizationData;
        const authData: AuthData = {
            token: token.accessToken,
            refreshToken: token.refreshToken,
            expiresAt: jwtPayload.exp
                ? Utils.getTokenExpireDate(jwtPayload.exp)
                : new Date(Date.now() + 60 * 60 * 1000),
            sessionStartedAt: this.resolveSessionStartedAt(
                existing?.sessionStartedAt,
                token.accessToken
            ),
        };

        this._localStorageService.authorizationData = authData;
        if (authData.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
    }

    get isTokenExpired(): boolean {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token) {
            return true;
        }
        if (authData.expiresAt) {
            return new Date(authData.expiresAt) <= new Date();
        }
        return this.isJwtExpired(authData.token);
    }

    get isTokenExpiringSoon(): boolean {
        if (this.isTokenExpired) {
            return true;
        }
        const authData = this._localStorageService.authorizationData;
        if (authData?.expiresAt) {
            return (
                new Date(authData.expiresAt).getTime() - Date.now() <=
                this.tokenRefreshBufferMs
            );
        }
        return this.isJwtExpiringSoon(authData?.token);
    }

    /** Returns a usable access token, refreshing proactively when close to expiry (Aya-aligned). */
    async getValidAccessToken(): Promise<string | null> {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token?.trim()) {
            return null;
        }
        if (!this.isTokenExpiringSoon) {
            return authData.token;
        }
        if (!authData.refreshToken?.trim()) {
            console.warn(
                '[clinic-auth] Access token expiring soon but no refreshToken is stored — sign in again via dev-app.'
            );
            return this.isTokenExpired ? null : authData.token;
        }
        const refreshed = await this.refreshSession('proactive');
        if (refreshed) {
            return refreshed;
        }
        return this.isTokenExpired ? null : authData.token;
    }

    isAuthRefreshRequest(url: string): boolean {
        return this.isIdentityRefreshRequest(url);
    }

    isIdentityRefreshRequest(url: string): boolean {
        const normalized = (url || '').toLowerCase();
        return normalized.includes('/api/v1/auth/identity/session/refresh');
    }

    isLegacyRefreshRequest(url: string): boolean {
        const normalized = (url || '').toLowerCase();
        return normalized.includes('/api/v1/auth/refresh-token');
    }

    isLegacyRefreshToken(refreshToken: string | null | undefined): boolean {
        const payload = this.parseJwtPayload(String(refreshToken || '').trim());
        return payload?.['type'] === 'refresh_token';
    }

    isIdentityRefreshToken(refreshToken: string | null | undefined): boolean {
        const trimmed = String(refreshToken || '').trim();
        if (!trimmed || this.isLegacyRefreshToken(trimmed)) {
            return false;
        }
        if (/^AMf[-_]|^AEu/i.test(trimmed)) {
            return true;
        }
        return !this.parseJwtPayload(trimmed)?.['companyId'];
    }

    /**
     * Single-flight session refresh used by JwtInterceptor (proactive), ErrorInterceptor (401),
     * and periodic refresh. All concurrent callers receive the same Promise and result.
     */
    refreshSession(_trigger = 'on-demand'): Promise<string | null> {
        if (this.refreshSessionPromise) {
            return this.refreshSessionPromise;
        }
        this.refreshSessionPromise = this.performSessionRefresh()
            .catch((err) => {
                console.warn('[clinic-auth] session refresh error', err);
                return null;
            })
            .finally(() => {
                this.refreshSessionPromise = null;
            });
        return this.refreshSessionPromise;
    }

    /** True while a refreshSession() operation is in flight (for diagnostics/tests). */
    isSessionRefreshInProgress(): boolean {
        return this.refreshSessionPromise !== null;
    }

    startPeriodicSessionRefresh(): void {
        if (typeof window === 'undefined' || this.sessionRefreshTimer) {
            return;
        }
        if (!this.refreshToken?.trim()) {
            return;
        }
        this.sessionRefreshTimer = setInterval(() => {
            if (!this.refreshToken?.trim()) {
                this.stopPeriodicSessionRefresh();
                return;
            }
            void this.refreshSessionIfNeeded();
        }, this.sessionRefreshIntervalMs);
    }

    stopPeriodicSessionRefresh(): void {
        if (this.sessionRefreshTimer) {
            clearInterval(this.sessionRefreshTimer);
            this.sessionRefreshTimer = null;
        }
    }

    private async refreshSessionIfNeeded(): Promise<void> {
        if (!this.refreshToken?.trim()) {
            return;
        }
        if (!this.isTokenExpiringSoon) {
            return;
        }
        await this.refreshSession('periodic');
    }

    private async performSessionRefresh(): Promise<string | null> {
        const authData = this._localStorageService.authorizationData;
        const storedRefresh = authData?.refreshToken?.trim();

        if (!storedRefresh) {
            return authData?.token && !this.isTokenExpired ? authData.token : null;
        }

        if (!this._localStorageService.machineId) {
            this._localStorageService.machineId = crypto.randomUUID();
        }

        if (
            this.isLegacyAccessToken(authData?.token) ||
            this.isLegacyRefreshToken(storedRefresh)
        ) {
            console.warn('[clinic-auth] Legacy token detected — sign in again.');
            this._localStorageService.authorizationData = null;
            return null;
        }

        if (this.isIdentityRefreshToken(storedRefresh)) {
            try {
                const response = await firstValueFrom(
                    this.refreshSessionViaIdentityApi(storedRefresh)
                );
                const idToken = (response.token || '').trim();
                if (idToken) {
                    this.persistAuthDataFromSessionRefresh(response, storedRefresh);
                    return idToken;
                }
            } catch (err) {
                console.warn('[clinic-auth] identity session refresh failed', err);
            }
        }

        return authData?.token && !this.isTokenExpired ? authData.token : null;
    }

    private isLegacyAccessToken(token: string | null | undefined): boolean {
        const payload = this.parseJwtPayload(String(token || '').trim());
        if (!payload) {
            return false;
        }
        return !!payload['companyId'] && payload['type'] !== 'refresh_token';
    }

    /** Firebase / Entra identity token (not legacy HS256 company_auth JWT). */
    isIdentityAccessToken(token?: string | null): boolean {
        const raw = String(token ?? this.authToken ?? '').trim();
        if (!raw || this.isLegacyAccessToken(raw)) {
            return false;
        }
        const payload = this.parseJwtPayload(raw);
        if (!payload) {
            return false;
        }
        const iss = String(payload['iss'] ?? '');
        if (iss.includes('securetoken.google.com')) {
            return true;
        }
        if (payload['oid']) {
            return true;
        }
        return !payload['companyId'];
    }

    /** Active workspace org for identity tokens (handoff or current clinic session). */
    resolveActiveOrganizationId(): string | null {
        return getLaunchRequestedCompanyId();
    }

    private persistAuthDataFromSessionRefresh(
        response: SessionRefreshResponse,
        fallbackRefresh: string
    ): void {
        const accessToken = (response.token || '').trim();
        if (!accessToken) {
            return;
        }

        let expiresAt: Date;
        if (response.expiresAt) {
            const parsed = Date.parse(response.expiresAt);
            expiresAt = Number.isFinite(parsed)
                ? new Date(parsed)
                : new Date(Date.now() + 60 * 60 * 1000);
        } else if (response.expiresIn) {
            expiresAt = new Date(Date.now() + response.expiresIn * 1000);
        } else {
            try {
                const exp = Utils.parseJwt(accessToken).exp;
                expiresAt = exp
                    ? Utils.getTokenExpireDate(exp)
                    : new Date(Date.now() + 60 * 60 * 1000);
            } catch {
                expiresAt = new Date(Date.now() + 60 * 60 * 1000);
            }
        }

        const existing = this._localStorageService.authorizationData;
        this._localStorageService.authorizationData = {
            token: accessToken,
            refreshToken: (response.refreshToken || fallbackRefresh).trim(),
            expiresAt,
            sessionStartedAt: this.resolveSessionStartedAt(
                existing?.sessionStartedAt,
                accessToken
            ),
        };
        if (this.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
    }

    /** Backfill sessionStartedAt for sessions created before this field existed. */
    ensureSessionStartedAt(): void {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token?.trim() || authData.sessionStartedAt) {
            return;
        }
        this._localStorageService.authorizationData = {
            ...authData,
            sessionStartedAt: this.resolveSessionStartedAt(
                undefined,
                authData.token
            ),
        };
    }

    /** When the current clinic session began (preserved across token refresh). */
    get sessionStartedAt(): Date | null {
        this.ensureSessionStartedAt();
        const raw = this._localStorageService.authorizationData?.sessionStartedAt;
        if (!raw) {
            return null;
        }
        const parsed = new Date(raw);
        return Number.isFinite(parsed.getTime()) ? parsed : null;
    }

    /** Milliseconds since sessionStartedAt, or null if unknown. */
    getSessionDurationMs(): number | null {
        const startedAt = this.sessionStartedAt;
        return startedAt ? Date.now() - startedAt.getTime() : null;
    }

    private resolveSessionStartedAt(
        existing: string | undefined,
        accessToken: string
    ): string {
        if (existing) {
            return existing;
        }
        const issuedAt = Number(this.parseJwtPayload(accessToken)?.['iat']);
        if (Number.isFinite(issuedAt) && issuedAt > 0) {
            return new Date(issuedAt * 1000).toISOString();
        }
        return new Date().toISOString();
    }

    private isJwtExpired(token: string): boolean {
        const payload = this.parseJwtPayload(token);
        const exp = Number(payload?.['exp']);
        if (!exp) {
            return false;
        }
        return exp * 1000 <= Date.now();
    }

    private isJwtExpiringSoon(token: string | undefined): boolean {
        if (!token) {
            return true;
        }
        const payload = this.parseJwtPayload(token);
        const exp = Number(payload?.['exp']);
        if (!exp) {
            return false;
        }
        return exp * 1000 - Date.now() <= this.tokenRefreshBufferMs;
    }

    private parseJwtPayload(token: string): Record<string, unknown> | null {
        try {
            return Utils.parseJwt(token) as Record<string, unknown>;
        } catch {
            return null;
        }
    }

    /**
     * Accept auth handoff from wa-account-v2 at /app-redirect.
     * Supports encrypted blob (?q=&d=) and readable Entra tokens (?token=&refreshToken=).
     */
    hydrateAuthFromQueryParams(params: {
        token?: string | null;
        refreshToken?: string | null;
        q?: string | null;
        d?: string | null;
    }): boolean {
        const rawToken = String(params.token || '').trim();
        if (rawToken) {
            if (this.isLegacyAccessToken(rawToken)) {
                return false;
            }
            this.persistAuthData({
                accessToken: rawToken,
                refreshToken: String(params.refreshToken || '').trim(),
            });
            this.applyMachineIdFromQueryParam(params.d);
            if (this.refreshToken?.trim()) {
                this.startPeriodicSessionRefresh();
            }
            return this.hasReadableAuthSession();
        }

        const encrypted = this.decodeQueryParam(params.q);
        if (!encrypted) {
            return false;
        }

        this._localStorageService.authorizationDataEncrypted = encrypted;
        this.applyMachineIdFromQueryParam(params.d);
        const ok = this.hasReadableAuthSession();
        if (ok && this.isLegacyAccessToken(this.authToken)) {
            this._localStorageService.authorizationData = null;
            return false;
        }
        if (ok && !this.refreshToken?.trim()) {
            console.warn(
                '[clinic-auth] Session handoff has access token but no refreshToken — periodic refresh and 401 recovery will not run until you sign in again via dev-app.'
            );
        } else if (ok && this.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
        if (ok) {
            this.ensureSessionStartedAt();
        }
        return ok;
    }

    private decodeQueryParam(value: string | null | undefined): string {
        const raw = String(value || '').trim();
        if (!raw) {
            return '';
        }
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }

    private applyMachineIdFromQueryParam(machineIdParam: string | null | undefined): void {
        const decoded = this.decodeQueryParam(machineIdParam);
        if (decoded) {
            this._localStorageService.machineId = decoded;
            return;
        }
        if (!this._localStorageService.machineId) {
            this._localStorageService.machineId = crypto.randomUUID();
        }
    }

    hasReadableAuthSession(): boolean {
        try {
            const authData = this._localStorageService.authorizationData;
            return !!authData?.token?.trim();
        } catch {
            this._localStorageService.authorizationData = null;
            return false;
        }
    }

    appendEncryptedAuthInfo(
        url: string,
        patientId?: string,
        handoff?: { userId?: string | null; companyId?: string | null }
    ): string {
        const queryParams = [
            `q=${encodeURIComponent(this._localStorageService.authorizationDataEncrypted)}`
        ];
        if (this._localStorageService.machineId) {
            queryParams.push(`d=${encodeURIComponent(this._localStorageService.machineId)}`);
        }
        if (patientId?.trim()) {
            queryParams.push(`id=${encodeURIComponent(patientId.trim())}`);
        }
        const org = this.resolveCrossAppHandoff(handoff);
        if (org.companyId) {
            queryParams.push(`companyId=${encodeURIComponent(org.companyId)}`);
        }
        if (org.userId) {
            queryParams.push(`userId=${encodeURIComponent(org.userId)}`);
        }
        return `${url}/app-redirect?${queryParams.join('&')}`;
    }

    private resolveCrossAppHandoff(handoff?: {
        userId?: string | null;
        companyId?: string | null;
    }): { userId?: string; companyId?: string } {
        const userId = handoff?.userId?.trim() || undefined;
        let companyId =
            handoff?.companyId?.trim() ||
            getLaunchRequestedCompanyId() ||
            undefined;
        if (!companyId) {
            const jwtCompanyId = this.parseJwtPayload(this.authToken)?.['companyId'];
            if (typeof jwtCompanyId === 'string' && jwtCompanyId.trim()) {
                companyId = jwtCompanyId.trim();
            }
        }
        return { userId, companyId };
    }
}

export interface AuthData {
    token: string;
    refreshToken: string;
    expiresAt: Date;
    /** ISO timestamp when this clinic session started (set at login/handoff, kept on refresh). */
    sessionStartedAt?: string;
}

/** Delegation row from GET .../auth/currentUser (`delegatedUsers` / `delegatedByUsers`). */
export interface DelegatedUser {
    delegationId: string;
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    title: string;
    createdAt: string;
}

export class User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    signature?: UserSignature;
    /** Users who may act on behalf of this user (current user is delegator). */
    delegatedUsers?: DelegatedUser[];
    /** Users who have delegated access to the current user (current user is delegate). */
    delegatedByUsers?: DelegatedUser[];
    phone: string;
    designation: string;
    title: string;
    status: UserStatus;
    type: UserType;
    globalAdmin: boolean;
    isOwner: boolean;
    npi: string;
    companyName: string;
    companyAddress: string;
    companyId: string;
    signatureKey?: string;
    isScheduleResource?: boolean;
    /** From GET currentUser; grants this user access to biller app and coverage flows. */
    hasBillerAppAccess?: boolean;
    isSigningPhysician?: boolean;
    openaiApiKey?: string;
    openaiApiUrl?: string;
    constructor(userType: UserType = UserType.BillingPhysician, title: string = '') {
        this.type = userType;
        this.title = title;
    }
}

export interface UserSignature {
    base64String: string;
    contentType: string;
}

export interface CompanyDetails {
    id: string;
    name: string;
    phone: string;
    fax: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    /** Default encounter/schedule length in minutes (GET users/{id}/companies → company.duration). */
    duration?: number;
    /** PHI Charts organization id (from GET users/{id}/companies → company.associatedChartsCompanyId). */
    associatedChartsCompanyId?: string;
    /** When true, outbound SMS uses Voidfix instead of Twilio (GET users/{id}/companies). */
    isVoidFix?: boolean;
    /** Optional Voidfix iMessage line id for the organization. */
    iMessageLineId?: string;
}

export interface UserCompany {
    userId: string;
    isPinned: boolean;
    isPrimary?: boolean;
    isHidden: boolean;
    companyId: string;
    companyName: string;
    phone?: string;
    fax?: string;
    company?: CompanyDetails;
}

export interface CompanyProductLicense {
    subscriptionStatus: SubscriptionStatus;
    name: string;
    isEnabled: boolean;
    qty: number;
    totalAllowedBillingPhysicians: number;
    activeBillingPhysicians: number;
    purchaseChannel: PurchaseChannel;
    percentValue: number;
}

export enum SubscriptionStatus {
    Active = 1,
    Suspended
}

export enum PurchaseChannel {
    CommercialDirect = 1,
    CommercialIndirect
}

export enum UserStatus {
    Active = 1,
    Pending,
    Suspended
}

export enum UserType {
    BillingPhysician = 1,
    Nurse,
    MedicalAssistent,
    NonMedicalPersonal
}